import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';

type LangCode = 'en' | 'ru' | 'de' | 'fr' | 'es' | 'it' | 'sv' | 'no';

type LandingCopy = {
  nav: { support: string; download: string; screens: string; features: string; login: string; registration: string; language: string; };
  hero: { lead: string; appStore: string; googlePlay: string; };
  concept: { title: string; body: string; cta: string; };
  mission: { title: string; body: string; cta: string; };
  shots: { aria: string; items: [string, string, string]; slideLabel: string; };
  features: { title: string; items: [string, string, string]; };
  download: { title: string; };
  support: {
    title: string; name: string; email: string; message: string; send: string;
    invalidName: string; invalidEmail: string; invalidMessage: string;
  };
};

const COPY: Record<LangCode, LandingCopy> = {
  en: {
    nav: { support: 'Support', download: 'Download', screens: 'Screens', features: 'Features', login: 'Login', registration: 'Registration', language: 'Language' },
    hero: {
      lead: 'A mobile app that shows how far your friend or your courier is away from you — in minutes.',
      appStore: 'App Store',
      googlePlay: 'Google Play',
    },
    concept: {
      title: 'Timesta Concept',
      body: 'Timesta shows the exact time between you and your friends, giving you the opportunity to easily track arrivals for a meeting or a party.',
      cta: 'Read more',
    },
    mission: {
      title: 'Our mission',
      body: 'Timesta can also be used for business purposes. It gives anyone the perfect opportunity to track the time of courier deliveries at a minimal cost.',
      cta: 'Read more',
    },
    shots: {
      aria: 'Application screens',
      items: ['Map screen', 'Friends nearby', 'Profile screen'],
      slideLabel: 'Slide',
    },
    features: {
      title: 'Timesta features',
      items: [
        'Calculate exact distance to your friends.',
        'Show estimated time to reach the meeting point.',
        'Notify about courier arrivals and deliveries.',
      ],
    },
    download: { title: 'Download Timesta' },
    support: {
      title: 'Support', name: 'Name', email: 'Email', message: 'Message', send: 'Send',
      invalidName: 'Please enter your name.', invalidEmail: 'Please enter a valid email.', invalidMessage: 'Please enter a message.',
    },
  },
  ru: {
    nav: { support: 'Поддержка', download: 'Скачать', screens: 'Экраны', features: 'Возможности', login: 'Вход', registration: 'Регистрация', language: 'Язык' },
    hero: {
      lead: 'Мобильное приложение, которое показывает, насколько далеко от вас находится ваш друг или курьер — в минутах.',
      appStore: 'App Store',
      googlePlay: 'Google Play',
    },
    concept: {
      title: 'Концепция Timesta',
      body: 'Timesta показывает точное время между вами и вашими друзьями, помогая удобно отслеживать прибытие на встречу или вечеринку.',
      cta: 'Подробнее',
    },
    mission: {
      title: 'Наша миссия',
      body: 'Timesta можно использовать и для бизнеса. Это простой способ отслеживать время курьерских доставок с минимальными затратами.',
      cta: 'Подробнее',
    },
    shots: { aria: 'Экраны приложения', items: ['Экран карты', 'Друзья рядом', 'Экран профиля'], slideLabel: 'Слайд' },
    features: {
      title: 'Возможности Timesta',
      items: [
        'Рассчитывает точное расстояние до ваших близких.',
        'Показывает примерное время до точки встречи.',
        'Уведомляет о прибытии курьеров и доставок.',
      ],
    },
    download: { title: 'Скачать Timesta' },
    support: {
      title: 'Поддержка', name: 'Имя', email: 'Электронная почта', message: 'Сообщение', send: 'Отправить',
      invalidName: 'Пожалуйста, укажите имя.', invalidEmail: 'Введите корректный email.', invalidMessage: 'Введите сообщение.',
    },
  },
  de: {
    nav: { support: 'Support', download: 'Download', screens: 'Screens', features: 'Funktionen', login: 'Login', registration: 'Registrierung', language: 'Sprache' },
    hero: {
      lead: 'Eine mobile App, die zeigt, wie weit dein Freund oder dein Kurier in Minuten von dir entfernt ist.',
      appStore: 'App Store',
      googlePlay: 'Google Play',
    },
    concept: {
      title: 'Timesta Konzept',
      body: 'Timesta zeigt die genaue Zeit zwischen dir und deinen Freunden und hilft dir, Ankünfte für Treffen oder Partys einfach zu verfolgen.',
      cta: 'Mehr erfahren',
    },
    mission: {
      title: 'Unsere Mission',
      body: 'Timesta kann auch geschäftlich genutzt werden. So lassen sich Kurierlieferungen einfach und kostengünstig zeitlich verfolgen.',
      cta: 'Mehr erfahren',
    },
    shots: { aria: 'App-Bildschirme', items: ['Kartenansicht', 'Freunde in der Nähe', 'Profilansicht'], slideLabel: 'Folie' },
    features: {
      title: 'Timesta Funktionen',
      items: [
        'Exakte Entfernung zu deinen Freunden berechnen.',
        'Geschätzte Zeit bis zum Treffpunkt anzeigen.',
        'Über Ankünfte von Kurieren und Lieferungen informieren.',
      ],
    },
    download: { title: 'Timesta herunterladen' },
    support: {
      title: 'Support', name: 'Name', email: 'E-Mail', message: 'Nachricht', send: 'Senden',
      invalidName: 'Bitte gib deinen Namen ein.', invalidEmail: 'Bitte gib eine gültige E-Mail ein.', invalidMessage: 'Bitte gib eine Nachricht ein.',
    },
  },
  fr: {
    nav: { support: 'Support', download: 'Télécharger', screens: 'Écrans', features: 'Fonctionnalités', login: 'Connexion', registration: 'Inscription', language: 'Langue' },
    hero: {
      lead: 'Une application mobile qui indique à quelle distance de vous se trouve votre ami ou votre coursier — en minutes.',
      appStore: 'App Store',
      googlePlay: 'Google Play',
    },
    concept: {
      title: 'Concept Timesta',
      body: 'Timesta affiche le temps exact entre vous et vos amis pour suivre facilement les arrivées à une rencontre ou à une fête.',
      cta: 'En savoir plus',
    },
    mission: {
      title: 'Notre mission',
      body: 'Timesta peut aussi être utilisé pour des besoins professionnels. Il permet de suivre les livraisons de coursiers à moindre coût.',
      cta: 'En savoir plus',
    },
    shots: { aria: 'Écrans de l’application', items: ['Écran carte', 'Amis à proximité', 'Écran profil'], slideLabel: 'Diapositive' },
    features: {
      title: 'Fonctionnalités Timesta',
      items: [
        'Calculer la distance exacte jusqu’à vos proches.',
        'Afficher le temps estimé jusqu’au point de rencontre.',
        'Notifier l’arrivée des coursiers et des livraisons.',
      ],
    },
    download: { title: 'Télécharger Timesta' },
    support: {
      title: 'Support', name: 'Nom', email: 'E-mail', message: 'Message', send: 'Envoyer',
      invalidName: 'Veuillez saisir votre nom.', invalidEmail: 'Veuillez saisir un e-mail valide.', invalidMessage: 'Veuillez saisir un message.',
    },
  },
  es: {
    nav: { support: 'Soporte', download: 'Descargar', screens: 'Pantallas', features: 'Funciones', login: 'Iniciar sesión', registration: 'Registro', language: 'Idioma' },
    hero: {
      lead: 'Una aplicación móvil que muestra a cuántos minutos de distancia está tu amigo o tu mensajero.',
      appStore: 'App Store',
      googlePlay: 'Google Play',
    },
    concept: {
      title: 'Concepto Timesta',
      body: 'Timesta muestra el tiempo exacto entre tú y tus amigos para seguir fácilmente las llegadas a una reunión o fiesta.',
      cta: 'Leer más',
    },
    mission: {
      title: 'Nuestra misión',
      body: 'Timesta también puede utilizarse para negocios. Ofrece una forma sencilla y económica de seguir los tiempos de entrega de mensajeros.',
      cta: 'Leer más',
    },
    shots: { aria: 'Pantallas de la aplicación', items: ['Pantalla de mapa', 'Amigos cercanos', 'Pantalla de perfil'], slideLabel: 'Diapositiva' },
    features: {
      title: 'Funciones de Timesta',
      items: [
        'Calcular la distancia exacta hasta tus amigos.',
        'Mostrar el tiempo estimado para llegar al punto de encuentro.',
        'Notificar la llegada de mensajeros y entregas.',
      ],
    },
    download: { title: 'Descargar Timesta' },
    support: {
      title: 'Soporte', name: 'Nombre', email: 'Correo electrónico', message: 'Mensaje', send: 'Enviar',
      invalidName: 'Introduce tu nombre.', invalidEmail: 'Introduce un correo válido.', invalidMessage: 'Introduce un mensaje.',
    },
  },
  it: {
    nav: { support: 'Supporto', download: 'Download', screens: 'Schermate', features: 'Funzionalità', login: 'Login', registration: 'Registrazione', language: 'Lingua' },
    hero: {
      lead: 'Un’app mobile che mostra in quanti minuti il tuo amico o il tuo corriere si trova da te.',
      appStore: 'App Store',
      googlePlay: 'Google Play',
    },
    concept: {
      title: 'Concetto Timesta',
      body: 'Timesta mostra il tempo esatto tra te e i tuoi amici, così puoi seguire facilmente gli arrivi a un incontro o a una festa.',
      cta: 'Scopri di più',
    },
    mission: {
      title: 'La nostra missione',
      body: 'Timesta può essere usata anche per il business. Offre un modo semplice ed economico per seguire i tempi delle consegne dei corrieri.',
      cta: 'Scopri di più',
    },
    shots: { aria: 'Schermate dell’app', items: ['Schermata mappa', 'Amici nelle vicinanze', 'Schermata profilo'], slideLabel: 'Slide' },
    features: {
      title: 'Funzionalità Timesta',
      items: [
        'Calcolare la distanza esatta dai tuoi amici.',
        'Mostrare il tempo stimato per raggiungere il punto d’incontro.',
        'Notificare arrivi di corrieri e consegne.',
      ],
    },
    download: { title: 'Scarica Timesta' },
    support: {
      title: 'Supporto', name: 'Nome', email: 'Email', message: 'Messaggio', send: 'Invia',
      invalidName: 'Inserisci il tuo nome.', invalidEmail: 'Inserisci un’email valida.', invalidMessage: 'Inserisci un messaggio.',
    },
  },
  sv: {
    nav: { support: 'Support', download: 'Ladda ner', screens: 'Skärmar', features: 'Funktioner', login: 'Logga in', registration: 'Registrering', language: 'Språk' },
    hero: {
      lead: 'En mobilapp som visar hur långt bort din vän eller kurir är från dig — i minuter.',
      appStore: 'App Store',
      googlePlay: 'Google Play',
    },
    concept: {
      title: 'Timesta-konceptet',
      body: 'Timesta visar exakt tid mellan dig och dina vänner så att du enkelt kan följa ankomster till möten eller fester.',
      cta: 'Läs mer',
    },
    mission: {
      title: 'Vårt uppdrag',
      body: 'Timesta kan även användas för företag. Det är ett enkelt och kostnadseffektivt sätt att följa kurirleveranser i tid.',
      cta: 'Läs mer',
    },
    shots: { aria: 'Appskärmar', items: ['Karta', 'Vänner i närheten', 'Profil'], slideLabel: 'Bild' },
    features: {
      title: 'Timesta-funktioner',
      items: [
        'Beräkna exakt avstånd till dina vänner.',
        'Visa beräknad tid till mötesplatsen.',
        'Avisera om kurir- och leveransankomster.',
      ],
    },
    download: { title: 'Ladda ner Timesta' },
    support: {
      title: 'Support', name: 'Namn', email: 'E-post', message: 'Meddelande', send: 'Skicka',
      invalidName: 'Ange ditt namn.', invalidEmail: 'Ange en giltig e-postadress.', invalidMessage: 'Ange ett meddelande.',
    },
  },
  no: {
    nav: { support: 'Support', download: 'Last ned', screens: 'Skjermer', features: 'Funksjoner', login: 'Logg inn', registration: 'Registrering', language: 'Språk' },
    hero: {
      lead: 'En mobilapp som viser hvor langt unna vennen din eller budet ditt er fra deg — i minutter.',
      appStore: 'App Store',
      googlePlay: 'Google Play',
    },
    concept: {
      title: 'Timesta-konseptet',
      body: 'Timesta viser nøyaktig tid mellom deg og vennene dine, slik at du enkelt kan følge ankomster til møter eller fester.',
      cta: 'Les mer',
    },
    mission: {
      title: 'Vårt oppdrag',
      body: 'Timesta kan også brukes i virksomheter. Det gir en enkel og rimelig måte å følge leveringstider for budtjenester på.',
      cta: 'Les mer',
    },
    shots: { aria: 'Appskjermer', items: ['Kartskjerm', 'Venner i nærheten', 'Profilskjerm'], slideLabel: 'Lysbilde' },
    features: {
      title: 'Timesta-funksjoner',
      items: [
        'Beregn nøyaktig avstand til vennene dine.',
        'Vis beregnet tid til møtestedet.',
        'Varsle om ankomst av bud og leveranser.',
      ],
    },
    download: { title: 'Last ned Timesta' },
    support: {
      title: 'Support', name: 'Navn', email: 'E-post', message: 'Melding', send: 'Send',
      invalidName: 'Skriv inn navnet ditt.', invalidEmail: 'Skriv inn en gyldig e-postadresse.', invalidMessage: 'Skriv inn en melding.',
    },
  },
};

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly langStorageKey = 'timesta.lang';
  private shotTimer: number | null = null;

  readonly langOptions: Array<{ code: LangCode; label: string }> = [
    { code: 'en', label: 'English' },
    { code: 'ru', label: 'Русский' },
    { code: 'de', label: 'Deutsch' },
    { code: 'fr', label: 'Français' },
    { code: 'es', label: 'Español' },
    { code: 'it', label: 'Italiano' },
    { code: 'sv', label: 'Svenska' },
    { code: 'no', label: 'Norsk' },
  ];
  lang: LangCode = 'en';

  readonly nowYear = new Date().getFullYear();
  readonly shotSources = [
    { src: 'assets/images/shot_map.webp', srcset: 'assets/images/shot_map.webp 1x, assets/images/shot_map_2x.webp 2x' },
    { src: 'assets/images/shot_list.webp', srcset: 'assets/images/shot_list.webp 1x, assets/images/shot_list_2x.webp 2x' },
    { src: 'assets/images/shot_profile.webp', srcset: 'assets/images/shot_profile.webp 1x, assets/images/shot_profile_2x.webp 2x' },
  ] as const;

  readonly supportForm = this.fb.group({
    name: this.fb.nonNullable.control('', { validators: [Validators.maxLength(80)] }),
    email: this.fb.nonNullable.control('', { validators: [Validators.maxLength(120)] }),
    message: this.fb.nonNullable.control('', { validators: [Validators.required, Validators.maxLength(4000)] }),
  });

  activeShot = 1;

  get c(): LandingCopy {
    return COPY[this.lang];
  }

  ngOnInit(): void {
    this.lang = this.resolveInitialLang();
    this.startShotAutoplay();
  }

  ngOnDestroy(): void {
    this.stopShotAutoplay();
  }

  setLang(code: LangCode): void {
    this.lang = code;
    try {
      localStorage.setItem(this.langStorageKey, code);
    } catch {}
    document.documentElement.lang = code;
  }

  scrollTo(id: string): void {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onDownload(platform: 'appstore' | 'googleplay'): void {
    const fallback = platform === 'appstore' ? 'https://www.apple.com/app-store/' : 'https://play.google.com/store';
    window.open(fallback, '_blank', 'noopener,noreferrer');
  }

  onSubmitSupport(): void {
    if (this.supportForm.controls.message.invalid) {
      this.supportForm.controls.message.markAsTouched();
      return;
    }

    const message = (this.supportForm.value.message ?? '').trim();
    const subject = encodeURIComponent('Timesta Support');
    const body = encodeURIComponent(message);
    window.location.href = `mailto:support@timesta.com?subject=${subject}&body=${body}`;
  }

  goShot(index: number): void {
    this.activeShot = index;
    this.restartShotAutoplay();
  }

  isPrevShot(index: number): boolean {
    return index === this.prevShotIndex();
  }

  isNextShot(index: number): boolean {
    return index === this.nextShotIndex();
  }

  goPortal(route: 'login' | 'register'): void {
    const root = this.getRootDomainFromHost(window.location.hostname);
    const proto = window.location.protocol || 'https:';
    window.location.href = `${proto}//my.${root}/${route}`;
  }

  private prevShotIndex(): number {
    return (this.activeShot - 1 + this.shotSources.length) % this.shotSources.length;
  }

  private nextShotIndex(): number {
    return (this.activeShot + 1) % this.shotSources.length;
  }

  private startShotAutoplay(): void {
    this.stopShotAutoplay();
    this.shotTimer = window.setInterval(() => {
      this.activeShot = this.nextShotIndex();
    }, 4200);
  }

  private stopShotAutoplay(): void {
    if (this.shotTimer !== null) {
      window.clearInterval(this.shotTimer);
      this.shotTimer = null;
    }
  }

  private restartShotAutoplay(): void {
    this.startShotAutoplay();
  }

  private resolveInitialLang(): LangCode {
    try {
      const stored = localStorage.getItem(this.langStorageKey) as LangCode | null;
      if (stored && stored in COPY) {
        document.documentElement.lang = stored;
        return stored;
      }
    } catch {}

    const host = (window.location.hostname || '').toLowerCase();
    const fallback: LangCode = host.endsWith('.ru') ? 'ru' : 'en';
    document.documentElement.lang = fallback;
    return fallback;
  }

  private getRootDomainFromHost(hostname: string): string {
    const h = String(hostname || '').trim().toLowerCase();
    if (!h) return 'timesta.org';
    const parts = h.replace(/^www\./, '').split('.').filter(Boolean);
    if (parts.length <= 2) return parts.join('.');
    return parts.slice(-2).join('.');
  }
}
